import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import styles from "../../../styles/components/Modal/LeadSettings/AddLeadModal.module.css";
import CloseIcon from "@mui/icons-material/Close";
import { Notification } from "../../UI/Notifications";
import { LoadingCircle } from "../../UI/Custom/LoadingCircle";
import { DropdownSelector } from "../../UI/DropdownSelector";

import { getAdmins } from "../../../utils/api/Admin/getAdminsApi";
import { getAllDesksApi } from "../../../utils/api/Admin/desk/getAllDesksApi";
import { getAffiliate } from "../../../utils/api/Affiliate/getAffiliateApi";
import { handleError } from "../../../utils/custom/errorHandler";
import { LangContext } from "../../../context/LangContext";
import { mapGeoOptions } from "../../../utils/geoCountryNames";

const API_BASE_URL = process.env.REACT_APP_BASE_API_URL;

const translations = {
    ru: {
        title: "Экспорт лидов",
        formatLabel: "Формат",
        chunkSizeLabel: "Chunk size",
        managerLabel: "Менеджер",
        deskLabel: "Деск",
        affiliatorLabel: "Аффилиат",
        saleStatusLabel: "Sale статус",
        retenStatusLabel: "Retention статус",
        geoLabel: "GEO",
        submit: "Экспортировать",
        submitting: "Экспортируем...",
        selectPlaceholder: "Не выбрано",
        formatPlaceholder: "Выберите формат",
        chunkInvalid: "Chunk size должен быть числом >= 1",
        exportFailed: "Экспорт не удался",
    },
    en: {
        title: "Export leads",
        formatLabel: "Format",
        chunkSizeLabel: "Chunk size",
        managerLabel: "Manager",
        deskLabel: "Desk",
        affiliatorLabel: "Affiliator",
        saleStatusLabel: "Sale status",
        retenStatusLabel: "Retention status",
        geoLabel: "GEO",
        submit: "Export",
        submitting: "Exporting...",
        selectPlaceholder: "Not selected",
        formatPlaceholder: "Select format",
        chunkInvalid: "Chunk size must be a number >= 1",
        exportFailed: "Export failed",
    },
};

export const ExportLeadsModal = ({ onClose, onSuccess, initialFilters }) => {
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;

    const modalRef = useRef(null);

    const [formData, setFormData] = useState({
        format: "csv",
        chunk_size: "1000",

        manager_id: "",
        desk_id: "",
        affiliator_id: "",
        sale_status: "",
        reten_status: "",
        geo: "",
    });

    const [managerOptions, setManagerOptions] = useState([]);
    const [deskOptions, setDeskOptions] = useState([]);
    const [affiliatorOptions, setAffiliatorOptions] = useState([]);
    const [saleStatusOptions, setSaleStatusOptions] = useState([]);
    const [retenStatusOptions, setRetenStatusOptions] = useState([]);
    const [geoOptions, setGeoOptions] = useState([]);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getToken = () => localStorage.getItem("token");

    useEffect(() => {
        if (!initialFilters) return;

        setFormData((prev) => ({
            ...prev,
            manager_id: initialFilters.manager_id ?? prev.manager_id,
            desk_id: initialFilters.desk_id ?? prev.desk_id,
            affiliator_id: initialFilters.affiliator_id ?? prev.affiliator_id,
            sale_status: initialFilters.sale_status ?? prev.sale_status,
            reten_status: initialFilters.reten_status ?? prev.reten_status,
            geo: initialFilters.geo ?? prev.geo,
        }));
    }, [initialFilters]);

    const normalizeList = (json) => {
        if (Array.isArray(json)) return json;
        if (json && Array.isArray(json.data)) return json.data;
        if (json && Array.isArray(json.items)) return json.items;
        return [];
    };

    const mapToOptions = (items) => {
        return (items || [])
            .map((it) => {
                if (typeof it === "string" || typeof it === "number") {
                    const v = String(it).trim();
                    if (!v) return null;
                    return { value: v, label: v };
                }

                if (it && typeof it === "object") {
                    const value = it.value ?? it.id ?? it.key ?? it.code ?? it.name;
                    const label =
                        it.label ?? it.name ?? it.title ?? (value != null ? String(value) : "");

                    if (value === undefined || value === null || String(value).trim() === "") {
                        return null;
                    }
                    return { value: String(value), label: String(label) };
                }

                return null;
            })
            .filter(Boolean);
    };

    useEffect(() => {
        const loadDropdowns = async () => {
            try {
                const [adminsRes, desksRes, affiliatesRes] = await Promise.allSettled([
                    getAdmins(),
                    getAllDesksApi(),
                    getAffiliate(),
                ]);

                if (adminsRes.status === "fulfilled") {
                    const admins = adminsRes.value.data || adminsRes.value;
                    setManagerOptions(
                        (admins || []).map((admin) => ({
                            value: String(admin.id),
                            label: `${admin.first_name} ${admin.last_name}`.trim(),
                        }))
                    );
                }

                if (desksRes.status === "fulfilled") {
                    const desksData = desksRes.value.data || desksRes.value;
                    setDeskOptions(
                        (desksData || []).map((desk) => ({
                            value: String(desk.id),
                            label: `${desk.name || ""} (${desk.description || ""})`.trim(),
                        }))
                    );
                }

                if (affiliatesRes.status === "fulfilled") {
                    const affiliates = affiliatesRes.value.data || affiliatesRes.value;
                    setAffiliatorOptions(
                        (affiliates || []).map((affiliate) => ({
                            value: String(affiliate.id),
                            label:
                                affiliate.first_name || affiliate.last_name
                                    ? `${affiliate.first_name || ""} ${affiliate.last_name || ""}`.trim()
                                    : affiliate.email,
                        }))
                    );
                }
            } catch (e) {
                // silent: dropdowns optional
            }
        };

        loadDropdowns();
    }, []);

    useEffect(() => {
        const loadFilters = async () => {
            try {
                const token = getToken();

                const endpoints = [
                    `${API_BASE_URL}/common/lead/filters/retention-statuses`,
                    `${API_BASE_URL}/common/lead/filters/sale-statuses`,
                    `${API_BASE_URL}/common/lead/filters/geos`,
                ];

                const [retenRes, saleRes, geoRes] = await Promise.allSettled(
                    endpoints.map((url) =>
                        fetch(url, {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                Accept: "application/json",
                            },
                        })
                    )
                );

                if (saleRes.status === "fulfilled" && saleRes.value.ok) {
                    const json = await saleRes.value.json().catch(() => null);
                    setSaleStatusOptions(mapToOptions(normalizeList(json)));
                }

                if (retenRes.status === "fulfilled" && retenRes.value.ok) {
                    const json = await retenRes.value.json().catch(() => null);
                    setRetenStatusOptions(mapToOptions(normalizeList(json)));
                }

                if (geoRes.status === "fulfilled" && geoRes.value.ok) {
                    const json = await geoRes.value.json().catch(() => null);
                    setGeoOptions(mapGeoOptions(normalizeList(json), langSelected));
                }
            } catch (e) {
                // silent
            }
        };

        loadFilters();
    }, [langSelected]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleFieldChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: null, general: null }));
    };

    const triggerFileDownload = (url) => {
        if (!url) return;

        try {
            const link = document.createElement("a");

            const resolvedUrl =
                url.startsWith("http://") || url.startsWith("https://")
                    ? url
                    : new URL(url, window.location.origin).toString();

            link.href = resolvedUrl;

            try {
                const urlObj = new URL(resolvedUrl);
                const pathname = urlObj.pathname || "";
                const filename = pathname.substring(pathname.lastIndexOf("/") + 1) || "leads_export";
                link.setAttribute("download", filename);
            } catch (e) {
                link.setAttribute("download", "leads_export");
            }

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const formatOptions = useMemo(
        () => [
            { value: "csv", label: "CSV" },
            { value: "xlsx", label: "XLSX" },
            { value: "json", label: "JSON" },
        ],
        []
    );

    const renderSelect = (label, value, options, onChange, placeholder) => (
        <div className={styles.inputRow}>
            <label>{label}:</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{ width: "100%", padding: "8px", borderRadius: 6 }}
            >
                <option value="">{placeholder || t.selectPlaceholder}</option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );

    const renderGeoSelect = (label, value, options, onChange) => (
        <div className={styles.inputRow}>
            <label>{label}:</label>
            <DropdownSelector
                filterName="geo"
                selectedValue={value}
                handleFilterChange={(_, nextValue) => onChange(nextValue)}
                options={options}
            />
        </div>
    );

    const handleSubmit = async () => {
        setErrors({});

        const chunk = parseInt(String(formData.chunk_size || "1000"), 10);
        if (Number.isNaN(chunk) || chunk < 1) {
            setErrors({ chunk_size: t.chunkInvalid });
            return;
        }

        setIsSubmitting(true);

        try {
            const token = getToken();

            const params = new URLSearchParams();
            params.set("format", String(formData.format || "csv"));
            params.set("chunk_size", String(chunk));

            if (formData.manager_id) params.set("manager_id", String(formData.manager_id));
            if (formData.desk_id) params.set("desk_id", String(formData.desk_id));
            if (formData.affiliator_id) params.set("affiliator_id", String(formData.affiliator_id));
            if (formData.sale_status) params.set("sale_status", String(formData.sale_status));
            if (formData.reten_status) params.set("reten_status", String(formData.reten_status));
            if (formData.geo) params.set("geo", String(formData.geo));

            const url = `${API_BASE_URL}/admin/lead/leads/export?${params.toString()}`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw errorData || { message: t.exportFailed };
            }

            const data = await response.json().catch(() => ({}));

            const fileUrl = data.file_url || data.fileUrl || null;
            if (fileUrl) {
                triggerFileDownload(fileUrl);
            }

            if (onSuccess) {
                onSuccess(data);
            }

            onClose();
        } catch (err) {
            handleError(err, setErrors);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.modal}>
            {errors.general && <Notification message={errors.general} type={"error"} />}

            <div className={styles.filtersContainer} ref={modalRef}>
                <div className={styles.modalLabel}>
                    <p>{t.title}</p>
                    <div onClick={onClose}>
                        <CloseIcon />
                    </div>
                </div>

                <div className={styles.modalContent}>
                    {renderSelect(
                        t.formatLabel,
                        formData.format,
                        formatOptions,
                        (v) => handleFieldChange("format", v),
                        t.formatPlaceholder
                    )}

                    <div className={styles.inputRow}>
                        <label>{t.chunkSizeLabel}:</label>
                        <input
                            type="number"
                            min="1"
                            value={formData.chunk_size}
                            onChange={(e) => handleFieldChange("chunk_size", e.target.value)}
                            style={{ width: "100%", padding: "8px", borderRadius: 6 }}
                        />
                        {errors.chunk_size && <div style={{ color: "red", marginTop: 4 }}>{errors.chunk_size}</div>}
                    </div>

                    {renderSelect(
                        t.managerLabel,
                        formData.manager_id,
                        managerOptions,
                        (v) => handleFieldChange("manager_id", v)
                    )}
                    {renderSelect(
                        t.deskLabel,
                        formData.desk_id,
                        deskOptions,
                        (v) => handleFieldChange("desk_id", v)
                    )}
                    {renderSelect(
                        t.affiliatorLabel,
                        formData.affiliator_id,
                        affiliatorOptions,
                        (v) => handleFieldChange("affiliator_id", v)
                    )}

                    {renderSelect(
                        t.saleStatusLabel,
                        formData.sale_status,
                        saleStatusOptions,
                        (v) => handleFieldChange("sale_status", v)
                    )}
                    {renderSelect(
                        t.retenStatusLabel,
                        formData.reten_status,
                        retenStatusOptions,
                        (v) => handleFieldChange("reten_status", v)
                    )}
                    {renderGeoSelect(
                        t.geoLabel,
                        formData.geo,
                        geoOptions,
                        (v) => handleFieldChange("geo", v)
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <div className={styles.confirmationWrapper}>
                        <button
                            className={styles.submitButton}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting && (
                                <span style={{ marginRight: 8, display: "inline-flex" }}>
                                    <LoadingCircle size={16} thickness={2} />
                                </span>
                            )}
                            {isSubmitting ? t.submitting : t.submit}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
